import qs from 'qs';
import { getLocale, strLocaleCompare } from '@rcm/i18n-client';
import { globalApi } from '@rcm/redux-global';
import getAppState from 'app-state';
import {
    CartCountryRule,
    CartDiscountRule,
    CartGeoCode,
    CartGeoCodeFilter,
    CartOrderShippingRule,
    CartProductCategory
} from 'cart-core';
import { PROFILE } from './constants';
import { Shipping } from './types';

const locale = getLocale();
const siteId = getAppState<number>('site.siteId');

const api = globalApi.injectEndpoints({
    endpoints: (build) => ({

        // TODO: These need to be namespaced in a way that will work in
        //       conjunction with RTKQ generated query hooks.

        calculate: build.query<{
            data: {
                bvSubTotal: number;
                couponDiscount: number;
                discount: number;
                formatted: {
                    bvSubTotal: string;
                    couponDiscount: string;
                    discount: string;
                    pvSubTotal: string;
                    shipping: string;
                    subTotal: string;
                    tax: string;
                    total: string;
                }
                message: string;
                pvSubTotal: number;
                shipping: number;
                subTotal: number;
                subTotalCalculated: boolean;
                tax: number;
                total: number;
                totalCalculated: boolean;
            };
            messages: [];
        }, {
            discountPercent: number;
            shipping: {
                city: string;
                country: string;
                geoCode: string;
                postalCode: string;
                state: string
            };
            shipVia: number;
            skus: { [skuNumber: string]: number };
        }>({
            query: ((arg) => ({
                url: '/api/cart/calculate',
                method: 'POST',
                body: {
                    discountPercent: arg.discountPercent,
                    id: -1,
                    items: mapSkuQuantitiesToOrderItems(arg.skus),
                    shipping: {
                        data: {
                            autoShipIsMonthly: false,
                            isAutoShip: false,
                            shipAddress: mapShipping(arg.shipping),
                            shipVia: arg.shipVia,
                        },
                        type: 'ShippingVistaBasic',
                    },
                    siteId,
                    user: {
                        data: [],
                        type: 'GenericDistributor'
                    },
                },
            })),
        }),

        findFieldTransferProductCategory: build.query<CartProductCategory, boolean>({
            query: (more = false) => ({
                url: `/api/resource/cart-product-category/findOne?${qs.stringify({
                    filter: {
                        where: {
                            locale,
                            name: `Field Transfer Products${more ? ' More' : ''}`
                        },
                    },
                })}`,
                method: 'GET',
            }),
            transformResponse: (data: CartProductCategory) => ({
                ...data,
                products: (data?.products ?? [])
                    .sort((a, b) => strLocaleCompare(a.name, b.name))
                    .map((product) => ({
                        ...product,
                        skus: product.skus
                            .sort((a, b) => strLocaleCompare(a.name, b.name))
                    }))
            })
        }),

        findCurrentCountryRule: build.query<CartCountryRule, void>({
            query: () => ({
                url: `/api/resource/country-rules/findOne?${qs.stringify({
                    filter: {
                        where: {
                            locale,
                        }
                    }
                })}`,
                method: 'GET',
            }),
        }),

        findCurrentDiscountRule: build.query<CartDiscountRule, void>({
            query: () => ({
                url: `/api/resource/discount-rules/findOne?${qs.stringify({
                    filter: {
                        where: {
                            locale,
                            profile: PROFILE,
                        }
                    }
                })}`,
                method: 'GET',
            }),
        }),

        findShipMethodRules: build.query<CartOrderShippingRule, {
            discountPercent: number;
            skus: { [skuNumber: string]: number };
            shipping: Shipping;
        }>({
            query: (arg) => ({
                url: `/api/resource/ship-method-rules/findOne?${qs.stringify({
                    filter: {
                        where: {
                            locale,
                            // Yes, these are supposed to be double-encoded.
                            // Yes, it's dumb. If somebody thinks they can fix
                            // the server to accept a sane encoding, be my
                            // guest. I wouldn't recommend it though.
                            profile: JSON.stringify(PROFILE),
                            order: JSON.stringify({
                                discountPercent: arg.discountPercent,
                                id: -1,
                                items: mapSkuQuantitiesToOrderItems(arg.skus),
                                shipping: mapShipping(arg.shipping),
                                siteId,
                            }),
                        },
                    },
                })}`,
                method: 'GET',
            }),
        }),

        findGeoCodes: build.query<CartGeoCode[], CartGeoCodeFilter>({
            query: ((filter) => ({
                url: `/api/resource/geo-code?${qs.stringify({ filter })}`,
                method: 'GET',
            })),
        }),
    }),
});

export default api;

export const {
    useCalculateQuery,
    useFindFieldTransferProductCategoryQuery,
    useFindCurrentCountryRuleQuery,
    useFindCurrentDiscountRuleQuery,
    useFindGeoCodesQuery,
    useFindShipMethodRulesQuery,
} = api;


const mapSkuQuantitiesToOrderItems = (skus: { [skuNumber: string]: number}) =>
    Object.entries(skus).map(([skuNumber, qty]) => ({
        data: null,
        qty,
        skuNumber,
        type: ''
    }));

const mapShipping = (shipping: Shipping) => ({
    addressLine1: '',
    addressLine2: '',
    city: shipping.city,
    country: shipping.country,
    geoCode: shipping.geoCode,
    isCommercial: null,
    nameLine: '',
    phone: '',
    postalCode: shipping.postalCode,
    state: shipping.state,
    type: 'BasicShipAddress',
    verified: false,
});
